import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import { setDevices } from '../store/deviceSlice';
import { setAdminData } from '../store/adminSlice';
import {
  PW_Devices_List_API,
  PW_Device_Decide_API,
  PW_Device_Relink_API,
  PW_Device_Revoke_API,
  PW_Device_Delete_API,
  PW_Device_Rename_API,
  PW_Stores_API,
} from '../Server/ApiV2';
import Spinner from './Spinner';
import usePresence from '../hooks/usePresence';

const fmtDateTime = (v) => {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch { return String(v); }
};

const humanizeAgo = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'never';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60 * 1000) return 'just now';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const PresenceDot = ({ uid, online, lastSeen, deviceLastSeenAt }) => {
  const isOnline = online.has(uid);
  const socketLs = lastSeen.get(uid);
  // Prefer the socket's lastSeen when it's newer than the REST snapshot.
  let effective = deviceLastSeenAt;
  if (socketLs && (!deviceLastSeenAt || new Date(socketLs) > new Date(deviceLastSeenAt))) {
    effective = socketLs;
  }
  const style = {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 8,
    verticalAlign: 'middle',
    background: isOnline ? '#00895e' : '#ef4444',
  };
  const title = isOnline ? 'Online' : `Last seen ${humanizeAgo(effective)}`;
  return <span style={style} title={title} />;
};

export default function Devices() {
  const dispatch = useDispatch();
  const devices = useSelector(s => s.device.devices);
  const stores = useSelector(s => s.admin.stores);
  const currentUserId = useSelector(s => s.user?.user?.id || s.user?.user?.userId || '');
  const { online, lastSeen } = usePresence();

  const [pageLoading, setPageLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [filter, setFilter] = useState('');
  const [sortCol, setSortCol] = useState('requestedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [busyId, setBusyId] = useState(null);

  const [picker, setPicker] = useState(null);
  const [pickerStoreId, setPickerStoreId] = useState('');
  const [renamer, setRenamer] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const refreshDevices = useCallback(async () => {
    const res = await PW_Devices_List_API();
    if (res?.data?.success) {
      const list = res.data.data?.devices || res.data.data || [];
      dispatch(setDevices(list));
    }
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [devRes, storeRes] = await Promise.all([
        PW_Devices_List_API(),
        stores.length ? Promise.resolve(null) : PW_Stores_API(),
      ]);
      if (cancelled) return;
      if (devRes?.data?.success) {
        const list = devRes.data.data?.devices || devRes.data.data || [];
        dispatch(setDevices(list));
      }
      if (storeRes?.data?.success) {
        dispatch(setAdminData({ stores: storeRes.data.data }));
      }
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch, stores.length]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortIcon = (col) => {
    if (sortCol !== col) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  // Rejected devices are deleted immediately, so history holds only revoked.
  const byTab = useMemo(() => ({
    pending: devices.filter(d => d.status === 'pending'),
    approved: devices.filter(d => d.status === 'approved'),
    history: devices.filter(d => d.status === 'revoked'),
  }), [devices]);

  const filtered = useMemo(() => {
    const list = byTab[activeTab] || [];
    const q = filter.trim().toLowerCase();
    const searched = !q ? list : list.filter(d =>
      d.deviceUid?.toLowerCase().includes(q) ||
      d.deviceName?.toLowerCase().includes(q) ||
      d.storeName?.toLowerCase().includes(q) ||
      d.model?.toLowerCase().includes(q)
    );
    return [...searched].sort((a, b) => {
      const va = String(a[sortCol] ?? '').toLowerCase();
      const vb = String(b[sortCol] ?? '').toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [byTab, activeTab, filter, sortCol, sortDir]);

  const openPicker = (mode, device) => {
    setPicker({ mode, device });
    setPickerStoreId(device.storeId || '');
  };
  const closePicker = () => { setPicker(null); setPickerStoreId(''); };

  const confirmPicker = async () => {
    if (!picker || !pickerStoreId) return;
    const { mode, device } = picker;
    setBusyId(device.id);
    try {
      if (mode === 'approve') {
        await PW_Device_Decide_API(device.id, { decision: 'approve', storeId: pickerStoreId, decidedBy: currentUserId });
      } else if (mode === 'relink') {
        await PW_Device_Relink_API(device.id, { storeId: pickerStoreId, decidedBy: currentUserId });
      }
      await refreshDevices();
      closePicker();
    } catch (err) { console.error('[Devices] Picker action failed:', err); }
    setBusyId(null);
  };

  // Reject fully removes the pending request. If the same device requests
  // access again, it lands as a fresh Pending row with a new DEV-### id.
  const handleReject = async (device) => {
    if (!window.confirm(`Reject and remove device request from "${device.deviceUid}"?`)) return;
    setBusyId(device.id);
    try {
      await PW_Device_Delete_API(device.id);
      await refreshDevices();
    } catch (err) { console.error('[Devices] Reject failed:', err); }
    setBusyId(null);
  };

  // Revoke keeps the row (Status=3) for audit — device is logged out on next
  // request but the record remains in the Revoked tab.
  const handleRevoke = async (device) => {
    if (!window.confirm(`Revoke access for device "${device.deviceName || device.deviceUid}"? The device will be logged out.`)) return;
    setBusyId(device.id);
    try {
      await PW_Device_Revoke_API(device.id, { decidedBy: currentUserId });
      await refreshDevices();
    } catch (err) { console.error('[Devices] Revoke failed:', err); }
    setBusyId(null);
  };

  const openRenamer = (device) => {
    setRenamer(device);
    setRenameValue(device.deviceName || '');
  };
  const closeRenamer = () => { setRenamer(null); setRenameValue(''); };

  const confirmRename = async () => {
    if (!renamer) return;
    const name = renameValue.trim();
    if (name.length > 100) return;
    setBusyId(renamer.id);
    try {
      await PW_Device_Rename_API(renamer.id, { deviceName: name });
      await refreshDevices();
      closeRenamer();
    } catch (err) { console.error('[Devices] Rename failed:', err); }
    setBusyId(null);
  };

  // Delete fully removes the row (any status). Separate from Revoke so admin
  // can clean up an approved / revoked / pending device permanently.
  const handleDelete = async (device) => {
    if (!window.confirm(`Delete device "${device.deviceName || device.deviceUid}"? This cannot be undone.`)) return;
    setBusyId(device.id);
    try {
      await PW_Device_Delete_API(device.id);
      await refreshDevices();
    } catch (err) { console.error('[Devices] Delete failed:', err); }
    setBusyId(null);
  };

  const statusChip = (s) => {
    const map = {
      pending:  { cls: 'chip chip-gold',    label: 'Pending' },
      approved: { cls: 'chip chip-green',   label: 'Approved' },
      rejected: { cls: 'chip chip-red',     label: 'Rejected' },
      revoked:  { cls: 'chip chip-red',     label: 'Revoked' },
    };
    const cfg = map[s] || { cls: 'chip', label: s || '—' };
    return <span className={cfg.cls}>{cfg.label}</span>;
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DevicesOutlinedIcon style={{ color: '#2d1b69' }} />
            Devices
          </h2>
          <div className="page-header-sub">Approve, relink, or revoke mobile devices requesting access</div>
        </div>
      </div>

      <div className="card mb-16">
        <div className="card-body" style={{ padding: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className={`btn ${activeTab === 'pending' ? 'btn-gold' : 'btn-outline'}`}
            onClick={() => setActiveTab('pending')}>
            Pending Requests ({byTab.pending.length})
          </button>
          <button
            className={`btn ${activeTab === 'approved' ? 'btn-gold' : 'btn-outline'}`}
            onClick={() => setActiveTab('approved')}>
            Approved Devices ({byTab.approved.length})
          </button>
          <button
            className={`btn ${activeTab === 'history' ? 'btn-gold' : 'btn-outline'}`}
            onClick={() => setActiveTab('history')}>
            Revoked ({byTab.history.length})
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-title">
          {activeTab === 'pending' && 'Pending Requests'}
          {activeTab === 'approved' && 'Approved Devices'}
          {activeTab === 'history' && 'Revoked'}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b6b80' }}>
            {filtered.length} of {(byTab[activeTab] || []).length}
          </span>
        </div>
        <div style={{ padding: '8px 16px' }}>
          <input
            className="form-input"
            placeholder="Search by UID, name, model, or store..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrap">
            {activeTab === 'pending' && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('deviceUid')}>Device UID{sortIcon('deviceUid')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('deviceName')}>Name{sortIcon('deviceName')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('model')}>Model{sortIcon('model')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('appVersion')}>Version{sortIcon('appVersion')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('requestedAt')}>Requested At{sortIcon('requestedAt')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(d => (
                    <tr key={d.id}>
                      <td data-label="Device UID" className="text-sm fw-600">
                        <PresenceDot uid={d.deviceUid} online={online} lastSeen={lastSeen} deviceLastSeenAt={d.lastSeenAt} />
                        {d.deviceUid}
                      </td>
                      <td data-label="Name">
                        {d.deviceName || <span className="text-muted">—</span>}
                        <button
                          className="btn-icon"
                          title="Rename device"
                          onClick={() => openRenamer(d)}
                          style={{ marginLeft: 6, verticalAlign: 'middle', border: 'none', background: 'transparent', cursor: 'pointer', padding: 2 }}>
                          <EditIcon fontSize="small" style={{ color: '#6b6b80' }} />
                        </button>
                      </td>
                      <td data-label="Model" className="text-sm">{d.model || '—'}</td>
                      <td data-label="Version" className="text-sm text-muted">{d.appVersion || '—'}</td>
                      <td data-label="Requested At" className="text-sm">{fmtDateTime(d.requestedAt)}</td>
                      <td data-label="Actions" style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn btn-success btn-sm"
                          disabled={busyId === d.id}
                          onClick={() => openPicker('approve', d)}>
                          <CheckCircleIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Approve
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ color: '#ef4444', borderColor: '#ef4444' }}
                          disabled={busyId === d.id}
                          onClick={() => handleReject(d)}>
                          <CancelIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Reject
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ color: '#6b6b80', borderColor: '#6b6b80' }}
                          disabled={busyId === d.id}
                          onClick={() => handleDelete(d)}>
                          <DeleteOutlinedIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#6b6b80' }}>No pending requests</td></tr>
                  )}
                </tbody>
              </table>
            )}

            {activeTab === 'approved' && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('deviceUid')}>Device UID{sortIcon('deviceUid')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('deviceName')}>Name{sortIcon('deviceName')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('storeName')}>Store{sortIcon('storeName')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('decidedByName')}>Approved By{sortIcon('decidedByName')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('decidedAt')}>Approved At{sortIcon('decidedAt')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastSeenAt')}>Last Seen{sortIcon('lastSeenAt')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(d => (
                    <tr key={d.id}>
                      <td data-label="Device UID" className="text-sm fw-600">
                        <PresenceDot uid={d.deviceUid} online={online} lastSeen={lastSeen} deviceLastSeenAt={d.lastSeenAt} />
                        {d.deviceUid}
                      </td>
                      <td data-label="Name">
                        {d.deviceName || <span className="text-muted">—</span>}
                        <button
                          className="btn-icon"
                          title="Rename device"
                          onClick={() => openRenamer(d)}
                          style={{ marginLeft: 6, verticalAlign: 'middle', border: 'none', background: 'transparent', cursor: 'pointer', padding: 2 }}>
                          <EditIcon fontSize="small" style={{ color: '#6b6b80' }} />
                        </button>
                      </td>
                      <td data-label="Store"><span className="chip chip-purple">{d.storeName || d.storeId || '—'}</span></td>
                      <td data-label="Approved By" className="text-sm">{d.decidedByName || d.decidedBy || '—'}</td>
                      <td data-label="Approved At" className="text-sm">{fmtDateTime(d.decidedAt)}</td>
                      <td data-label="Last Seen" className="text-sm text-muted">{fmtDateTime(d.lastSeenAt)}</td>
                      <td data-label="Actions" style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn btn-outline btn-sm"
                          disabled={busyId === d.id}
                          onClick={() => openPicker('relink', d)}>
                          <SwapHorizIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Relink
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ color: '#ef4444', borderColor: '#ef4444' }}
                          disabled={busyId === d.id}
                          onClick={() => handleRevoke(d)}>
                          <BlockIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Revoke
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ color: '#6b6b80', borderColor: '#6b6b80' }}
                          disabled={busyId === d.id}
                          onClick={() => handleDelete(d)}>
                          <DeleteOutlinedIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#6b6b80' }}>No approved devices</td></tr>
                  )}
                </tbody>
              </table>
            )}

            {activeTab === 'history' && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('deviceUid')}>Device UID{sortIcon('deviceUid')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('deviceName')}>Name{sortIcon('deviceName')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('model')}>Model{sortIcon('model')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortIcon('status')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('decidedByName')}>Decided By{sortIcon('decidedByName')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('decidedAt')}>Decided At{sortIcon('decidedAt')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(d => (
                    <tr key={d.id}>
                      <td data-label="Device UID" className="text-sm fw-600">{d.deviceUid}</td>
                      <td data-label="Name">{d.deviceName || '—'}</td>
                      <td data-label="Model" className="text-sm">{d.model || '—'}</td>
                      <td data-label="Status">{statusChip(d.status)}</td>
                      <td data-label="Decided By" className="text-sm">{d.decidedByName || d.decidedBy || '—'}</td>
                      <td data-label="Decided At" className="text-sm">{fmtDateTime(d.decidedAt)}</td>
                      <td data-label="Actions" style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn btn-success btn-sm"
                          disabled={busyId === d.id}
                          onClick={() => openPicker('approve', d)}>
                          <CheckCircleIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Approve
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ color: '#6b6b80', borderColor: '#6b6b80' }}
                          disabled={busyId === d.id}
                          onClick={() => handleDelete(d)}>
                          <DeleteOutlinedIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#6b6b80' }}>No revoked devices</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {picker && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(45, 27, 105, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={closePicker}>
          <div
            className="card"
            style={{ width: 'min(520px, 92vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <div className="card-title gold">
              {picker.mode === 'approve' ? 'Link device to store' : 'Move device to store'}
            </div>
            <div className="card-body" style={{ overflow: 'auto' }}>
              <div className="text-sm text-muted mb-16">
                Device: <b>{picker.device.deviceName || picker.device.deviceUid}</b>
              </div>
              {stores.length === 0 && (
                <div style={{ padding: 12, color: '#ef4444' }}>No stores available. Create a store first.</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {stores.map(s => (
                  <label
                    key={s.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      border: `1px solid ${pickerStoreId === s.id ? '#c9a227' : '#e5e5ef'}`,
                      background: pickerStoreId === s.id ? '#fdf7e3' : '#fff',
                      borderRadius: 6, cursor: 'pointer',
                    }}>
                    <input
                      type="radio"
                      name="device-store-picker"
                      checked={pickerStoreId === s.id}
                      onChange={() => setPickerStoreId(s.id)}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="fw-600">{s.name}</span>
                      <span className="text-sm text-muted">{s.code} · {s.city || s.location || '—'}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ padding: 12, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
              <button className="btn btn-secondary" onClick={closePicker}>Cancel</button>
              <button
                className="btn btn-success"
                disabled={!pickerStoreId || busyId === picker.device.id}
                onClick={confirmPicker}>
                {busyId === picker.device.id ? '...' : (picker.mode === 'approve' ? 'Approve & Link' : 'Relink')}
              </button>
            </div>
          </div>
        </div>
      )}

      {renamer && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(45, 27, 105, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={closeRenamer}>
          <div
            className="card"
            style={{ width: 'min(440px, 92vw)', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <div className="card-title gold">Rename device</div>
            <div className="card-body">
              <div className="text-sm text-muted mb-16">
                UID: <b style={{ wordBreak: 'break-all' }}>{renamer.deviceUid}</b>
              </div>
              <label className="form-label" htmlFor="rename-input">Friendly name</label>
              <input
                id="rename-input"
                className="form-input"
                autoFocus
                maxLength={100}
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') confirmRename();
                  if (e.key === 'Escape') closeRenamer();
                }}
                placeholder="e.g. Front counter · Manager POS · Store 3 register"
              />
              <div className="text-sm text-muted" style={{ marginTop: 6 }}>
                Leave blank to clear. Up to 100 characters.
              </div>
            </div>
            <div style={{ padding: 12, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
              <button className="btn btn-secondary" onClick={closeRenamer}>Cancel</button>
              <button
                className="btn btn-success"
                disabled={busyId === renamer.id}
                onClick={confirmRename}>
                {busyId === renamer.id ? '...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
